import { Injectable, NgZone, inject } from '@angular/core';

import { WINDOW } from '@frontend/vanilla/core';
import { Observable, Subject, Subscription, auditTime, fromEvent } from 'rxjs';

export class EventService {
    protected _subject$: Subject<EventTarget> | undefined;
    private subscription: Subscription | undefined;
    private _eventAuditTime = 150;

    private get subject(): Subject<EventTarget> {
        if (!this._subject$) {
            this.setup();
            return this._subject$;
        } else {
            return this._subject$;
        }
    }

    constructor(
        private zone: NgZone,
        private target: Element | Window,
        private event: string,
    ) {}

    subscribe(next?: (target: EventTarget) => void, error?: (error: unknown) => void, complete?: () => void): Subscription {
        return this.subject.subscribe({ next, error, complete });
    }

    asObservable(): Observable<EventTarget> {
        return this.subject.asObservable();
    }

    private listener(): void {
        this.zone.run(() => {
            if (!this._subject$ || !this._subject$.observed) {
                this.subscription?.unsubscribe();
                this._subject$ = undefined;
            } else {
                this._subject$.next(this.target);
            }
        });
    }

    private setup(): asserts this is this & { _subject$: Subject<EventTarget> } {
        this._subject$ = new Subject<EventTarget>();

        this.zone.runOutsideAngular(() => {
            this.subscription = fromEvent<EventTarget>(this.target, this.event)
                .pipe(auditTime(this._eventAuditTime))
                .subscribe(() => {
                    this.listener();
                });
        });
    }
}

@Injectable({ providedIn: 'root' })
export class WindowEventService {
    private zone = inject(NgZone);

    private readonly _window = inject(WINDOW);

    scroll = new EventService(this.zone, this._window, 'scroll');
    resize = new EventService(this.zone, this._window, 'resize');
}
